// Alarm sounds synthesised in the browser (architecture sprint-14 §8.1, B-7, owner decision 4: no audio files).
// Pure: each renders ONE loop period into a Float32Array, peak 0.9, starting and ending at a zero crossing of
// its envelope so `loop = true` never clicks. No dependency, nothing to fetch while the laptop is half asleep.

export type AlarmSoundKind = 'klaxon' | 'bell' | 'siren';

export const ALARM_PEAK = 0.9;
const TWO_PI = Math.PI * 2;
/** Short fades at every tone edge, so the square wave's steps do not click. */
const EDGE_FADE_SEC = 0.004;

const KLAXON = { lengthSec: 2.4, toneSec: 0.4, freqs: [440, 554] as const };
const BELL = { lengthSec: 2.4, strikeSec: 0.6, base: 880, partials: [1, 2.76, 5.4] as const, weights: [1, 0.5, 0.25] as const, decay: 6 };
const SIREN = { lengthSec: 1.6, low: 600, high: 1200 };

export const ALARM_SOUND_LENGTH_SEC: Record<AlarmSoundKind, number> = { klaxon: KLAXON.lengthSec, bell: BELL.lengthSec, siren: SIREN.lengthSec };

function edgeGain(tInTone: number, toneSec: number): number {
  return Math.min(1, tInTone / EDGE_FADE_SEC, (toneSec - tInTone) / EDGE_FADE_SEC);
}

function klaxon(out: Float32Array, rate: number): void {
  let phase = 0;
  for (let i = 0; i < out.length; i += 1) {
    const t = i / rate;
    const tone = Math.floor(t / KLAXON.toneSec);
    const freq = KLAXON.freqs[tone % KLAXON.freqs.length] ?? KLAXON.freqs[0];
    phase = (phase + freq / rate) % 1;
    const square = phase < 0.5 ? 1 : -1;
    out[i] = square * edgeGain(t - tone * KLAXON.toneSec, KLAXON.toneSec);
  }
}

function bell(out: Float32Array, rate: number): void {
  for (let i = 0; i < out.length; i += 1) {
    const t = i / rate;
    const strike = Math.floor(t / BELL.strikeSec);
    const ts = t - strike * BELL.strikeSec;
    let v = 0;
    BELL.partials.forEach((p, k) => {
      v += (BELL.weights[k] ?? 0) * Math.sin(TWO_PI * BELL.base * p * ts);
    });
    out[i] = v * Math.exp(-BELL.decay * ts) * edgeGain(ts, BELL.strikeSec);
  }
}

function siren(out: Float32Array, rate: number): void {
  let phase = 0;
  const half = SIREN.lengthSec / 2;
  for (let i = 0; i < out.length; i += 1) {
    const t = i / rate;
    // Up for the first half, down for the second: the frequency is continuous across the loop point.
    const x = t < half ? t / half : (SIREN.lengthSec - t) / half;
    const freq = SIREN.low + (SIREN.high - SIREN.low) * x;
    phase = (phase + freq / rate) % 1;
    out[i] = (2 * phase - 1) * edgeGain(t, SIREN.lengthSec);
  }
}

/** One loop period of the sound, normalised to a peak of 0.9. */
export function renderAlarmSound(kind: AlarmSoundKind, sampleRate: number): Float32Array {
  const out = new Float32Array(Math.round(ALARM_SOUND_LENGTH_SEC[kind] * sampleRate));
  if (kind === 'klaxon') klaxon(out, sampleRate);
  else if (kind === 'bell') bell(out, sampleRate);
  else siren(out, sampleRate);
  let peak = 0;
  for (const v of out) peak = Math.max(peak, Math.abs(v));
  if (peak > 0) for (let i = 0; i < out.length; i += 1) out[i] = ((out[i] ?? 0) / peak) * ALARM_PEAK;
  return out;
}

export const ALARM_SOUND_LABELS: Record<AlarmSoundKind, string> = { klaxon: 'Klaxon', bell: 'Bell', siren: 'Siren' };
